package com.gloopgo.game.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.Shadow
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.PointerId
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.gloopgo.game.domain.BlobAiController
import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.moderation.BlockedPlayersStore
import com.gloopgo.game.moderation.PlayerReportReason
import com.gloopgo.game.moderation.visiblePlayerName
import com.gloopgo.game.navigation.ConsumeMatchSystemBack
import com.gloopgo.game.engine.GloopPlayer
import com.gloopgo.game.engine.Vector2D
import com.gloopgo.game.network.MultiplayerRepository
import com.gloopgo.game.ui.theme.GameCanvasColors
import com.gloopgo.game.ui.theme.LocalGameCanvasColors
import com.gloopgo.game.viewmodel.GameViewModel
import gloopgo.shared.generated.resources.*
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import kotlin.math.max
import kotlin.math.min

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun GameScreen(
    playerName: String,
    useJoystick: Boolean = false,
    playerEmoji: String = "",
    playerColor: Long? = null,
    isSinglePlayer: Boolean = false,
    providedPlayerId: String? = null,
    onExitToMenu: () -> Unit = {},
    onToggleJoystick: (Boolean) -> Unit = {},
    bottomAdContent: @Composable (() -> Unit)? = null,
    viewModel: GameViewModel
) {
    ConsumeMatchSystemBack()
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val gameState = uiState.gameState
    val canvasColors = LocalGameCanvasColors.current
    val blockedPlayersStore: BlockedPlayersStore = koinInject()
    val repository: MultiplayerRepository = koinInject()
    val blockedPlayers by blockedPlayersStore.blocked.collectAsStateWithLifecycle()
    val blockedIds = remember(blockedPlayers) { blockedPlayers.map { it.uid }.toSet() }
    val blockedLabel = stringResource(Res.string.blocked_player_name)
    val safetyScope = rememberCoroutineScope()
    var safetyTarget by remember { mutableStateOf<Pair<String, String>?>(null) }

    LaunchedEffect(playerName, playerEmoji, playerColor, isSinglePlayer, providedPlayerId) {
        viewModel.initGame(
            playerName = playerName,
            providedPlayerId = providedPlayerId,
            playerEmoji = playerEmoji,
            playerColor = playerColor,
            isSinglePlayer = isSinglePlayer
        )
    }

    var isJoystickActive by rememberSaveable { mutableStateOf(useJoystick) }
    var pointerDirection by remember { mutableStateOf(Vector2D.ZERO) }
    var mousePosition by remember { mutableStateOf<Offset?>(null) }
    var keyboardDirection by remember { mutableStateOf(Vector2D.ZERO) }
    var joystickVector by remember { mutableStateOf(Vector2D.ZERO) }
    var lastAimDirection by remember { mutableStateOf(Vector2D(1f, 0f)) }
    var userZoomFactor by remember { mutableStateOf(1.0f) }
    var viewportSize by remember { mutableStateOf(Size.Zero) }
    val focusRequester = remember { FocusRequester() }
    val textMeasurer = rememberTextMeasurer()
    var showExitConfirmDialog by remember { mutableStateOf(false) }
    val actionHints = remember { getPlatformActionHints() }

    fun requestExit() {
        if (gameState.isGameOver) {
            onExitToMenu()
        } else {
            showExitConfirmDialog = true
        }
    }

    fun getAimWorldPos(cameraTarget: Vector2D, zoom: Float): Vector2D {
        val mPos = if (isJoystickActive) null else mousePosition
        val centerX = viewportSize.width / 2f
        val centerY = viewportSize.height / 2f
        return if (mPos != null && centerX > 0f && centerY > 0f && zoom > 0f) {
            val worldDx = (mPos.x - centerX) / zoom
            val worldDy = (mPos.y - centerY) / zoom
            cameraTarget + Vector2D(worldDx, worldDy)
        } else {
            cameraTarget + (lastAimDirection * 600f)
        }
    }

    // 60 FPS Simulation Loop
    LaunchedEffect(Unit) {
        focusRequester.requestFocus()
        var lastTimeNanos = 0L

        while (isActive) {
            withFrameNanos { nowNanos ->
                if (lastTimeNanos != 0L) {
                    val deltaSeconds = min((nowNanos - lastTimeNanos) / 1_000_000_000f, 0.05f)
                    val currentMillis = kotlin.time.Clock.System.now().toEpochMilliseconds()

                    if (!uiState.isPaused) {
                        val cameraTarget = gameState.cameraTarget
                        val zoom = (gameState.cameraZoom * userZoomFactor).coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
                        val targetWorld = if (!isJoystickActive && mousePosition != null) getAimWorldPos(cameraTarget, zoom) else null

                        val mPos = if (isJoystickActive) null else mousePosition
                        val centerX = viewportSize.width / 2f
                        val centerY = viewportSize.height / 2f
                        val pointerOffset = if (mPos != null && centerX > 0f && centerY > 0f) {
                            Vector2D(mPos.x - centerX, mPos.y - centerY)
                        } else {
                            null
                        }

                        if (isJoystickActive) {
                            if (joystickVector.length() > 0.01f) {
                                lastAimDirection = joystickVector.normalized()
                            }
                        } else {
                            if (pointerDirection.length() > 0.01f) {
                                lastAimDirection = pointerDirection.normalized()
                            } else if (keyboardDirection.length() > 0.01f) {
                                lastAimDirection = keyboardDirection.normalized()
                            }
                        }

                        val effectiveFallback = if (isJoystickActive) joystickVector else keyboardDirection

                        viewModel.onGameTick(
                            deltaTime = deltaSeconds,
                            targetWorldPos = targetWorld,
                            pointerOffset = pointerOffset,
                            fallbackDirection = effectiveFallback,
                            timestamp = currentMillis
                        )
                    }
                }
                lastTimeNanos = nowNanos
            }
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(canvasColors.arenaBackground)
    ) {
        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .focusRequester(focusRequester)
                .focusable()
                .pointerInput(isJoystickActive, uiState.isPaused) {
                if (uiState.isPaused) return@pointerInput
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        val pressedPointers = event.changes.filter { it.pressed }

                        // Multi-touch pinch zoom
                        if (pressedPointers.size >= 2) {
                            val p0 = pressedPointers[0].position
                            val p1 = pressedPointers[1].position
                            val prevP0 = pressedPointers[0].previousPosition
                            val prevP1 = pressedPointers[1].previousPosition
                            val curDx = p0.x - p1.x
                            val curDy = p0.y - p1.y
                            val currentDist = kotlin.math.sqrt(curDx * curDx + curDy * curDy)
                            val prevDx = prevP0.x - prevP1.x
                            val prevDy = prevP0.y - prevP1.y
                            val prevDist = kotlin.math.sqrt(prevDx * prevDx + prevDy * prevDy)
                            if (prevDist > 1f && currentDist > 1f) {
                                val zoomChange = currentDist / prevDist
                                userZoomFactor = (userZoomFactor * zoomChange).coerceIn(0.2f, 3.5f)
                            }
                        }

                        val primaryPointer = event.changes.firstOrNull()
                        if (primaryPointer != null) {
                            if (primaryPointer.pressed) {
                                focusRequester.requestFocus()
                            }
                            if (!isJoystickActive) {
                                val isTouch = primaryPointer.type == PointerType.Touch
                                if (isTouch) {
                                    if (primaryPointer.pressed) {
                                        val pos = primaryPointer.position
                                        mousePosition = pos
                                        val centerX = viewportSize.width / 2f
                                        val centerY = viewportSize.height / 2f
                                        if (centerX > 0f && centerY > 0f) {
                                            val dx = pos.x - centerX
                                            val dy = pos.y - centerY
                                            val dist = kotlin.math.sqrt(dx * dx + dy * dy)
                                            val deadzone = 12f
                                            if (dist > deadzone) {
                                                val maxDist = 200f
                                                val intensity = ((dist - deadzone) / (maxDist - deadzone)).coerceIn(0f, 1f)
                                                val dir = Vector2D(dx / dist, dy / dist)
                                                pointerDirection = dir * intensity
                                                lastAimDirection = dir
                                            } else {
                                                pointerDirection = Vector2D.ZERO
                                            }
                                        }
                                    } else {
                                        mousePosition = null
                                        pointerDirection = Vector2D.ZERO
                                    }
                                } else {
                                    // Mouse (Desktop / Web)
                                    val pos = primaryPointer.position
                                    mousePosition = pos
                                    val centerX = viewportSize.width / 2f
                                    val centerY = viewportSize.height / 2f
                                    if (centerX > 0f && centerY > 0f) {
                                        val dx = pos.x - centerX
                                        val dy = pos.y - centerY
                                        val dist = kotlin.math.sqrt(dx * dx + dy * dy)
                                        val deadzone = 12f
                                        if (dist > deadzone) {
                                            val maxDist = 200f
                                            val intensity = ((dist - deadzone) / (maxDist - deadzone)).coerceIn(0f, 1f)
                                            val dir = Vector2D(dx / dist, dy / dist)
                                            pointerDirection = dir * intensity
                                            lastAimDirection = dir
                                        } else {
                                            pointerDirection = Vector2D.ZERO
                                        }
                                    }
                                }
                            }

                            val scrollDelta = primaryPointer.scrollDelta.y
                            if (scrollDelta < 0f) {
                                userZoomFactor = (userZoomFactor * 1.12f).coerceIn(0.2f, 3.5f)
                            } else if (scrollDelta > 0f) {
                                userZoomFactor = (userZoomFactor / 1.12f).coerceIn(0.2f, 3.5f)
                            }
                        }
                    }
                }
            }
            .onPreviewKeyEvent { keyEvent ->
                if (showExitConfirmDialog) {
                    if (keyEvent.type == KeyEventType.KeyDown && keyEvent.key == Key.Escape) {
                        showExitConfirmDialog = false
                        return@onPreviewKeyEvent true
                    }
                    return@onPreviewKeyEvent false
                }

                if (isSinglePlayer && uiState.isPaused) {
                    if (keyEvent.type == KeyEventType.KeyDown) {
                        when (keyEvent.key) {
                            Key.P, Key.Escape -> {
                                viewModel.resumeGame()
                                return@onPreviewKeyEvent true
                            }
                        }
                    }
                    return@onPreviewKeyEvent false
                }

                val cameraTarget = gameState.cameraTarget
                val zoom = (gameState.cameraZoom * userZoomFactor).coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
                val targetWorld = getAimWorldPos(cameraTarget, zoom)

                when (keyEvent.type) {
                    KeyEventType.KeyDown -> {
                        when (keyEvent.key) {
                            Key.P -> {
                                if (isSinglePlayer && !gameState.isGameOver) {
                                    viewModel.togglePause()
                                    return@onPreviewKeyEvent true
                                }
                                false
                            }
                            Key.Spacebar -> {
                                if (!uiState.isPaused && !gameState.isGameOver && gameState.localPlayer?.canSplit == true && gameState.splitCooldownRemainingMs <= 0L) {
                                    viewModel.onSplit(targetWorld)
                                }
                                true
                            }
                            Key.E -> {
                                if (!uiState.isPaused && !gameState.isGameOver && gameState.localPlayer?.canEjectMass == true && gameState.ejectCooldownRemainingMs <= 0L) {
                                    viewModel.onEjectMass(targetWorld)
                                }
                                true
                            }
                            Key.DirectionUp -> {
                                if (!isJoystickActive && !uiState.isPaused) keyboardDirection = Vector2D(keyboardDirection.x, -1f)
                                true
                            }
                            Key.DirectionDown -> {
                                if (!isJoystickActive && !uiState.isPaused) keyboardDirection = Vector2D(keyboardDirection.x, 1f)
                                true
                            }
                            Key.DirectionLeft -> {
                                if (!isJoystickActive && !uiState.isPaused) keyboardDirection = Vector2D(-1f, keyboardDirection.y)
                                true
                            }
                            Key.DirectionRight -> {
                                if (!isJoystickActive && !uiState.isPaused) keyboardDirection = Vector2D(1f, keyboardDirection.y)
                                true
                            }
                            Key.J -> {
                                val nextState = !isJoystickActive
                                isJoystickActive = nextState
                                if (!nextState) {
                                    joystickVector = Vector2D.ZERO
                                }
                                onToggleJoystick(nextState)
                                focusRequester.requestFocus()
                                true
                            }
                            Key.Plus, Key.Equals, Key.NumPadAdd -> {
                                userZoomFactor = (userZoomFactor * 1.15f).coerceIn(0.2f, 3.5f)
                                true
                            }
                            Key.Minus, Key.NumPadSubtract -> {
                                userZoomFactor = (userZoomFactor / 1.15f).coerceIn(0.2f, 3.5f)
                                true
                            }
                            Key.Zero, Key.NumPad0 -> {
                                userZoomFactor = 1.0f
                                true
                            }
                            Key.Escape -> {
                                if (isSinglePlayer && !gameState.isGameOver) {
                                    viewModel.pauseGame()
                                } else {
                                    requestExit()
                                }
                                true
                            }
                            else -> false
                        }
                    }
                    KeyEventType.KeyUp -> {
                        when (keyEvent.key) {
                            Key.DirectionUp, Key.DirectionDown -> {
                                if (!isJoystickActive) keyboardDirection = Vector2D(keyboardDirection.x, 0f)
                                true
                            }
                            Key.DirectionLeft, Key.DirectionRight -> {
                                if (!isJoystickActive) keyboardDirection = Vector2D(0f, keyboardDirection.y)
                                true
                            }
                            else -> false
                        }
                    }
                    else -> false
                }
            }
    ) {
        // Main Game Canvas
        Canvas(modifier = Modifier.fillMaxSize()) {
            viewportSize = size
            val cameraTarget = gameState.cameraTarget
            val zoom = (gameState.cameraZoom * userZoomFactor).coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
            val centerX = size.width / 2f
            val centerY = size.height / 2f

            // Background Arena Grid
            drawArenaGrid(
                cameraTarget = cameraTarget,
                zoom = zoom,
                centerX = centerX,
                centerY = centerY,
                worldWidth = viewModel.worldWidth,
                worldHeight = viewModel.worldHeight,
                colors = canvasColors
            )

            // Render Food Pellets
            for (pellet in gameState.pellets) {
                val screenX = centerX + (pellet.position.x - cameraTarget.x) * zoom
                val screenY = centerY + (pellet.position.y - cameraTarget.y) * zoom
                val screenRadius = max(3f, pellet.radius * zoom)

                if (screenX + screenRadius >= 0 && screenX - screenRadius <= size.width &&
                    screenY + screenRadius >= 0 && screenY - screenRadius <= size.height
                ) {
                    val massInt = pellet.mass.toInt().coerceIn(1, 10)

                    // Draw outer dot circle
                    drawCircle(
                        color = Color(pellet.color),
                        radius = screenRadius,
                        center = Offset(screenX, screenY)
                    )

                    // Draw subtle border for contrast
                    if (screenRadius >= 5f) {
                        drawCircle(
                            color = Color.Black.copy(alpha = 0.25f),
                            radius = screenRadius,
                            center = Offset(screenX, screenY),
                            style = Stroke(width = max(1f, 1f * zoom))
                        )
                    }

                    if (pellet.isHazard) {
                        // Hazard Pellet (Poop 💩 or Bomb 💣): Deducts 10 mass
                        val isBomb = pellet.emoji == "💣"
                        val auraColor = if (isBomb) Color(0xFFFF1744) else Color(0xFF8D6E63)

                        // Glowing hazard danger ring
                        drawCircle(
                            color = auraColor.copy(alpha = 0.80f),
                            radius = screenRadius + max(1.5f, 1.5f * zoom),
                            center = Offset(screenX, screenY),
                            style = Stroke(width = max(1.5f, 1.5f * zoom))
                        )

                        val hazardEmoji = if (pellet.emoji.isNotEmpty()) pellet.emoji else if (isBomb) "💣" else "💩"
                        if (screenRadius >= 6f) {
                            if (screenRadius >= 14f) {
                                // Zoomed in: Show hazard emoji and "-10" warning clearly underneath
                                val emojiFontSize = (screenRadius * 0.90f).toSp()
                                val emojiLayout = textMeasurer.measure(
                                    text = hazardEmoji,
                                    style = TextStyle(
                                        fontSize = emojiFontSize,
                                        textAlign = TextAlign.Center
                                    )
                                )
                                val numFontSize = (screenRadius * 0.38f).coerceAtLeast(8f).toSp()
                                val numLayout = textMeasurer.measure(
                                    text = "-10",
                                    style = TextStyle(
                                        color = Color(0xFFFF5252),
                                        fontSize = numFontSize,
                                        fontWeight = FontWeight.Black,
                                        textAlign = TextAlign.Center,
                                        shadow = Shadow(
                                            color = Color.Black.copy(alpha = 0.9f),
                                            blurRadius = 3f,
                                            offset = Offset(0.5f, 0.5f)
                                        )
                                    )
                                )
                                val totalHeight = emojiLayout.size.height + numLayout.size.height
                                drawText(
                                    textLayoutResult = emojiLayout,
                                    topLeft = Offset(
                                        screenX - emojiLayout.size.width / 2f,
                                        screenY - totalHeight / 2f
                                    )
                                )
                                drawText(
                                    textLayoutResult = numLayout,
                                    topLeft = Offset(
                                        screenX - numLayout.size.width / 2f,
                                        screenY - totalHeight / 2f + emojiLayout.size.height
                                    )
                                )
                            } else {
                                // Normal view: Prominent hazard emoji centered on the dot
                                val emojiFontSize = (screenRadius * 1.35f).toSp()
                                val emojiLayout = textMeasurer.measure(
                                    text = hazardEmoji,
                                    style = TextStyle(
                                        fontSize = emojiFontSize,
                                        textAlign = TextAlign.Center
                                    )
                                )
                                drawText(
                                    textLayoutResult = emojiLayout,
                                    topLeft = Offset(
                                        screenX - emojiLayout.size.width / 2f,
                                        screenY - emojiLayout.size.height / 2f
                                    )
                                )
                            }
                        }
                    } else if (massInt == 10) {
                        // 10s: Special golden aura and random food emoji
                        drawCircle(
                            color = Color(0xFFFFD600).copy(alpha = 0.65f),
                            radius = screenRadius + max(1.5f, 1.5f * zoom),
                            center = Offset(screenX, screenY),
                            style = Stroke(width = max(1.5f, 1.5f * zoom))
                        )

                        val emoji = pellet.getFoodEmoji()
                        if (screenRadius >= 7f) {
                            if (screenRadius >= 14f) {
                                // Zoomed in: Show food emoji and "10" clearly underneath
                                val emojiFontSize = (screenRadius * 0.95f).toSp()
                                val emojiLayout = textMeasurer.measure(
                                    text = emoji,
                                    style = TextStyle(
                                        fontSize = emojiFontSize,
                                        textAlign = TextAlign.Center
                                    )
                                )
                                val numFontSize = (screenRadius * 0.38f).coerceAtLeast(8f).toSp()
                                val numLayout = textMeasurer.measure(
                                    text = "10",
                                    style = TextStyle(
                                        color = Color.White,
                                        fontSize = numFontSize,
                                        fontWeight = FontWeight.Black,
                                        textAlign = TextAlign.Center,
                                        shadow = Shadow(
                                            color = Color.Black.copy(alpha = 0.85f),
                                            blurRadius = 3f,
                                            offset = Offset(0.5f, 0.5f)
                                        )
                                    )
                                )
                                val totalHeight = emojiLayout.size.height + numLayout.size.height
                                drawText(
                                    textLayoutResult = emojiLayout,
                                    topLeft = Offset(
                                        screenX - emojiLayout.size.width / 2f,
                                        screenY - totalHeight / 2f
                                    )
                                )
                                drawText(
                                    textLayoutResult = numLayout,
                                    topLeft = Offset(
                                        screenX - numLayout.size.width / 2f,
                                        screenY - totalHeight / 2f + emojiLayout.size.height
                                    )
                                )
                            } else {
                                // Normal view: Prominent food emoji centered on the dot
                                val emojiFontSize = (screenRadius * 1.35f).toSp()
                                val emojiLayout = textMeasurer.measure(
                                    text = emoji,
                                    style = TextStyle(
                                        fontSize = emojiFontSize,
                                        textAlign = TextAlign.Center
                                    )
                                )
                                drawText(
                                    textLayoutResult = emojiLayout,
                                    topLeft = Offset(
                                        screenX - emojiLayout.size.width / 2f,
                                        screenY - emojiLayout.size.height / 2f
                                    )
                                )
                            }
                        }
                    } else {
                        // 1 to 9: Number written directly on the dot
                        if (screenRadius >= 5f) {
                            val numFontSize = (screenRadius * 1.15f).coerceIn(7f, 18f).toSp()
                            val numLayout = textMeasurer.measure(
                                text = "$massInt",
                                style = TextStyle(
                                    color = Color.White,
                                    fontSize = numFontSize,
                                    fontWeight = FontWeight.Black,
                                    textAlign = TextAlign.Center,
                                    shadow = Shadow(
                                        color = Color.Black.copy(alpha = 0.85f),
                                        blurRadius = 2.5f,
                                        offset = Offset(0.5f, 0.5f)
                                    )
                                )
                            )
                            drawText(
                                textLayoutResult = numLayout,
                                topLeft = Offset(
                                    screenX - numLayout.size.width / 2f,
                                    screenY - numLayout.size.height / 2f
                                )
                            )
                        }
                    }
                }
            }

            // Render Ejected Masses
            for (ejected in gameState.ejectedMasses) {
                val screenX = centerX + (ejected.position.x - cameraTarget.x) * zoom
                val screenY = centerY + (ejected.position.y - cameraTarget.y) * zoom
                val screenRadius = max(4f, ejected.radius * zoom)

                if (screenX + screenRadius >= 0 && screenX - screenRadius <= size.width &&
                    screenY + screenRadius >= 0 && screenY - screenRadius <= size.height
                ) {
                    drawCircle(
                        color = Color(ejected.color),
                        radius = screenRadius,
                        center = Offset(screenX, screenY)
                    )
                    drawCircle(
                        color = Color.White.copy(alpha = 0.6f),
                        radius = screenRadius,
                        center = Offset(screenX, screenY),
                        style = Stroke(width = 1.5f * zoom)
                    )
                }
            }

            // Render All Gloops
            val allPlayers = mutableListOf<GloopPlayer>()
            allPlayers.addAll(gameState.remotePlayers)
            gameState.localPlayer?.let { if (it.isAlive) allPlayers.add(it) }

            val allCellsToRender = allPlayers.flatMap { player ->
                player.cells.map { cell -> Triple(player, cell, player.id == gameState.localPlayer?.id) }
            }.sortedBy { it.second.radius }

            for ((player, cell, isLocal) in allCellsToRender) {
                val shownName = visiblePlayerName(
                    name = player.name,
                    playerId = player.id,
                    localPlayerId = gameState.localPlayer?.id,
                    blockedIds = blockedIds,
                    blockedLabel = blockedLabel
                )
                val screenX = centerX + (cell.position.x - cameraTarget.x) * zoom
                val screenY = centerY + (cell.position.y - cameraTarget.y) * zoom
                val screenRadius = cell.radius * zoom

                if (screenX + screenRadius < 0 || screenX - screenRadius > size.width ||
                    screenY + screenRadius < 0 || screenY - screenRadius > size.height
                ) {
                    continue
                }

                val blobColor = Color(player.color)

                // Soft outer glow / aura
                drawCircle(
                    color = blobColor.copy(alpha = 0.25f),
                    radius = screenRadius + 4f * zoom,
                    center = Offset(screenX, screenY)
                )

                // Main cell body
                drawCircle(
                    color = blobColor,
                    radius = screenRadius,
                    center = Offset(screenX, screenY)
                )

                // High-contrast border
                drawCircle(
                    color = if (isLocal) Color.White else Color.Black.copy(alpha = 0.4f),
                    radius = screenRadius,
                    center = Offset(screenX, screenY),
                    style = Stroke(width = if (isLocal) 3.5f * zoom else 2f * zoom)
                )

                // Label: Name, Mass and Animal Emoji scaling proportionally with player circle
                if (player.emoji.isNotEmpty()) {
                    if (screenRadius < 26f) {
                        // For small/split cells, render the emoji prominently in the center of the circle
                        val emojiFontSize = (screenRadius * 1.35f).coerceAtLeast(12f).toSp()
                        val emojiLayout = textMeasurer.measure(
                            text = player.emoji,
                            style = TextStyle(
                                fontSize = emojiFontSize,
                                textAlign = TextAlign.Center
                            )
                        )
                        drawText(
                            textLayoutResult = emojiLayout,
                            topLeft = Offset(
                                screenX - emojiLayout.size.width / 2f,
                                screenY - emojiLayout.size.height / 2f
                            )
                        )
                    } else {
                        // Proportional scaling with the circle:
                        // Emoji occupies ~85% of radius height, name & mass underneath
                        val emojiFontSize = (screenRadius * 0.85f).toSp()
                        val emojiLayout = textMeasurer.measure(
                            text = player.emoji,
                            style = TextStyle(
                                fontSize = emojiFontSize,
                                textAlign = TextAlign.Center
                            )
                        )
                        val nameFontSizePx = minOf(
                            screenRadius * 0.20f,
                            (screenRadius * 1.5f) / (shownName.length.coerceAtLeast(1) * 0.65f)
                        ).coerceAtLeast(8f)
                        val nameLayout = textMeasurer.measure(
                            text = "$shownName\n${cell.mass.toInt()}",
                            style = TextStyle(
                                color = Color.White,
                                fontSize = nameFontSizePx.toSp(),
                                fontWeight = FontWeight.Bold,
                                textAlign = TextAlign.Center,
                                shadow = Shadow(
                                    color = Color.Black.copy(alpha = 0.85f),
                                    blurRadius = 4f,
                                    offset = Offset(1f, 1f)
                                )
                            )
                        )
                        val spacing = screenRadius * 0.04f
                        val totalHeight = emojiLayout.size.height + nameLayout.size.height + spacing
                        drawText(
                            textLayoutResult = emojiLayout,
                            topLeft = Offset(
                                screenX - emojiLayout.size.width / 2f,
                                screenY - totalHeight / 2f
                            )
                        )
                        drawText(
                            textLayoutResult = nameLayout,
                            topLeft = Offset(
                                screenX - nameLayout.size.width / 2f,
                                screenY - totalHeight / 2f + emojiLayout.size.height + spacing
                            )
                        )
                    }
                } else {
                    val nameFontSizePx = (screenRadius * 0.28f).coerceAtLeast(9f)
                    val textLayout = textMeasurer.measure(
                        text = "$shownName\n${cell.mass.toInt()}",
                        style = TextStyle(
                            color = Color.White,
                            fontSize = nameFontSizePx.toSp(),
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center,
                            shadow = Shadow(
                                color = Color.Black.copy(alpha = 0.85f),
                                blurRadius = 4f,
                                offset = Offset(1f, 1f)
                            )
                        )
                    )

                    drawText(
                        textLayoutResult = textLayout,
                        topLeft = Offset(
                            screenX - textLayout.size.width / 2f,
                            screenY - textLayout.size.height / 2f
                        )
                    )
                }
            }

            // Off-Screen Remote Player Indicators (Radar)
            for (remote in gameState.remotePlayers) {
                if (!remote.isAlive || remote.cells.isEmpty()) continue
                val rPos = remote.centerPosition
                val screenX = centerX + (rPos.x - cameraTarget.x) * zoom
                val screenY = centerY + (rPos.y - cameraTarget.y) * zoom

                val margin = 36f
                val isOffScreen = screenX < margin || screenX > size.width - margin ||
                        screenY < margin || screenY > size.height - margin

                if (isOffScreen && size.width > 0f && size.height > 0f) {
                    val dx = screenX - centerX
                    val dy = screenY - centerY
                    val angle = kotlin.math.atan2(dy, dx)
                    val cosA = kotlin.math.cos(angle)
                    val sinA = kotlin.math.sin(angle)

                    val halfW = (size.width / 2f) - margin
                    val halfH = (size.height / 2f) - margin
                    val scaleX = if (cosA != 0f) halfW / kotlin.math.abs(cosA) else Float.MAX_VALUE
                    val scaleY = if (sinA != 0f) halfH / kotlin.math.abs(sinA) else Float.MAX_VALUE
                    val scale = minOf(scaleX, scaleY)

                    val edgeX = centerX + cosA * scale
                    val edgeY = centerY + sinA * scale

                    val blobColor = Color(remote.color)
                    val indicatorRadius = 14f

                    // Indicator background circle & aura
                    drawCircle(
                        color = Color.Black.copy(alpha = 0.65f),
                        radius = indicatorRadius + 2f,
                        center = Offset(edgeX, edgeY)
                    )
                    drawCircle(
                        color = blobColor,
                        radius = indicatorRadius,
                        center = Offset(edgeX, edgeY)
                    )
                    drawCircle(
                        color = Color.White.copy(alpha = 0.9f),
                        radius = indicatorRadius,
                        center = Offset(edgeX, edgeY),
                        style = Stroke(width = 1.5f)
                    )

                    // Directional pointer triangle pointing outward toward the remote player
                    val pointerDist = indicatorRadius + 6f
                    val tipX = edgeX + cosA * pointerDist
                    val tipY = edgeY + sinA * pointerDist
                    val normalX = -sinA * 5f
                    val normalY = cosA * 5f
                    val baseX = edgeX + cosA * (indicatorRadius - 1f)
                    val baseY = edgeY + sinA * (indicatorRadius - 1f)

                    val path = Path().apply {
                        moveTo(tipX, tipY)
                        lineTo(baseX + normalX, baseY + normalY)
                        lineTo(baseX - normalX, baseY - normalY)
                        close()
                    }
                    drawPath(path = path, color = blobColor)

                    // Render emoji in indicator
                    if (remote.emoji.isNotEmpty()) {
                        val emojiLayout = textMeasurer.measure(
                            text = remote.emoji,
                            style = TextStyle(
                                fontSize = 12.sp,
                                textAlign = TextAlign.Center
                            )
                        )
                        drawText(
                            textLayoutResult = emojiLayout,
                            topLeft = Offset(
                                edgeX - emojiLayout.size.width / 2f,
                                edgeY - emojiLayout.size.height / 2f
                            )
                        )
                    }
                }
            }
        }

        // Safe Area HUD Overlay Container (Leaderboard, Close Button, Minimap, Action Buttons)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .windowInsetsPadding(
                    if (bottomAdContent != null) {
                        WindowInsets.safeDrawing.only(WindowInsetsSides.Horizontal + WindowInsetsSides.Top)
                    } else {
                        WindowInsets.safeDrawing
                    }
                )
                .focusProperties { canFocus = false }
        ) {
            // HUD: Top-Left Controls (Leave & Pause Above, Mass Readout Below)
            Column(
                modifier = Modifier
                    .padding(16.dp)
                    .align(Alignment.TopStart),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                horizontalAlignment = Alignment.Start
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Leave Game Button (Above Mass)
                    Card(
                        colors = CardDefaults.cardColors(containerColor = canvasColors.hudBackground),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        IconButton(
                            onClick = { requestExit() },
                            modifier = Modifier.size(38.dp)
                        ) {
                            Text(
                                text = "✕",
                                color = Color(0xFFFF5252),
                                fontSize = 16.sp,
                                fontWeight = FontWeight.Black
                            )
                        }
                    }

                    // Single Player Pause Button
                    if (isSinglePlayer && !gameState.isGameOver) {
                        Card(
                            colors = CardDefaults.cardColors(containerColor = canvasColors.hudBackground),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            IconButton(
                                onClick = { viewModel.togglePause() },
                                modifier = Modifier.size(38.dp)
                            ) {
                                Text(
                                    text = if (uiState.isPaused) "▶" else "⏸",
                                    color = if (uiState.isPaused) Color(0xFF00E676) else canvasColors.hudTextPrimary,
                                    fontSize = 16.sp,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    }
                }

                // Mass Readout (Below Close Button)
                gameState.localPlayer?.let { player ->
                    Card(
                        colors = CardDefaults.cardColors(containerColor = canvasColors.hudBackground),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Column(modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp)) {
                            Text(
                                text = stringResource(Res.string.game_hud_mass, player.totalMass.toInt()),
                                color = canvasColors.hudTextPrimary,
                                fontWeight = FontWeight.Bold,
                                fontSize = 18.sp
                            )
                        }
                    }
                }
            }

        // HUD: Top-Right Leaderboard & Mini-Map Container
        Column(
            modifier = Modifier
                .padding(16.dp)
                .align(Alignment.TopEnd),
            horizontalAlignment = Alignment.End,
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            // Leaderboard
            Card(
                colors = CardDefaults.cardColors(containerColor = canvasColors.hudBackground),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.widthIn(min = 160.dp, max = 220.dp)
            ) {
                Column(modifier = Modifier.padding(12.dp)) {
                    Text(
                        text = stringResource(Res.string.game_hud_leaderboard),
                        color = Color(0xFFFFD600),
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp
                    )
                    Spacer(modifier = Modifier.height(6.dp))
                    gameState.leaderboard.forEachIndexed { index, entry ->
                        val shownName = visiblePlayerName(
                            name = entry.name,
                            playerId = entry.id,
                            localPlayerId = gameState.localPlayer?.id,
                            blockedIds = blockedIds,
                            blockedLabel = blockedLabel
                        )
                        val canModerate = !entry.isLocalPlayer &&
                            entry.id.isNotEmpty() &&
                            entry.id != gameState.localPlayer?.id
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .then(
                                    if (canModerate) {
                                        Modifier.clickable {
                                            safetyTarget = entry.id to entry.name
                                        }
                                    } else {
                                        Modifier
                                    }
                                ),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text(
                                text = "${index + 1}. $shownName",
                                color = if (entry.isLocalPlayer) canvasColors.hudAccent else canvasColors.hudTextPrimary,
                                fontWeight = if (entry.isLocalPlayer) FontWeight.Bold else FontWeight.Normal,
                                fontSize = 12.sp,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                modifier = Modifier.weight(1f, fill = false)
                            )
                            Text(
                                text = "${entry.score}",
                                color = if (entry.isLocalPlayer) canvasColors.hudAccent else canvasColors.hudTextSecondary,
                                fontSize = 12.sp,
                                maxLines = 1,
                                softWrap = false,
                                modifier = Modifier.padding(start = 8.dp)
                            )
                        }
                    }
                }
            }

            // Mini-Map
            Card(
                colors = CardDefaults.cardColors(containerColor = canvasColors.hudBackground),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.size(90.dp)
            ) {
                Canvas(modifier = Modifier.fillMaxSize().padding(6.dp)) {
                    drawRect(
                        color = Color.White.copy(alpha = 0.3f),
                        size = size,
                        style = Stroke(width = 1.5f)
                    )

                    // Draw all other/remote players on the mini-map
                    for (remote in gameState.remotePlayers) {
                        if (!remote.isAlive || remote.cells.isEmpty()) continue
                        val rPos = remote.centerPosition
                        val rx = (rPos.x / viewModel.worldWidth).coerceIn(0f, 1f) * size.width
                        val ry = (rPos.y / viewModel.worldHeight).coerceIn(0f, 1f) * size.height
                        val rColor = Color(remote.color)
                        // Outer pulse/halo
                        drawCircle(
                            color = rColor.copy(alpha = 0.45f),
                            radius = 4f,
                            center = Offset(rx, ry)
                        )
                        // Inner solid dot
                        drawCircle(
                            color = rColor,
                            radius = 2.5f,
                            center = Offset(rx, ry)
                        )
                    }

                    val dotPos = if (gameState.localPlayer != null && gameState.localPlayer.isAlive && gameState.localPlayer.cells.isNotEmpty()) {
                        gameState.localPlayer.centerPosition
                    } else {
                        gameState.cameraTarget
                    }
                    val dotX = (dotPos.x / viewModel.worldWidth).coerceIn(0f, 1f) * size.width
                    val dotY = (dotPos.y / viewModel.worldHeight).coerceIn(0f, 1f) * size.height

                    // Local player indicator with high-contrast white ring
                    drawCircle(
                        color = Color.White,
                        radius = 4.5f,
                        center = Offset(dotX, dotY),
                        style = Stroke(width = 1.5f)
                    )
                    drawCircle(
                        color = if (gameState.localPlayer?.isAlive == true) Color(0xFF00E5FF) else Color(0xFFFF5252),
                        radius = 3.2f,
                        center = Offset(dotX, dotY)
                    )
                }
            }

            // Zoom Controls
            Card(
                colors = CardDefaults.cardColors(containerColor = canvasColors.hudBackground),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.width(90.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 2.dp, vertical = 2.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    val canZoomOut = userZoomFactor > 0.2001f
                    val canZoomIn = userZoomFactor < 3.4999f
                    IconButton(
                        onClick = { userZoomFactor = (userZoomFactor / 1.2f).coerceIn(0.2f, 3.5f) },
                        enabled = canZoomOut,
                        modifier = Modifier.size(28.dp)
                    ) {
                        Text(
                            "−",
                            color = if (canZoomOut) canvasColors.hudTextPrimary else canvasColors.hudTextSecondary.copy(alpha = 0.35f),
                            fontSize = 18.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    Text(
                        text = "${(userZoomFactor * 100).toInt()}%",
                        color = canvasColors.hudTextSecondary,
                        fontSize = 10.sp,
                        fontWeight = FontWeight.Bold
                    )
                    IconButton(
                        onClick = { userZoomFactor = (userZoomFactor * 1.2f).coerceIn(0.2f, 3.5f) },
                        enabled = canZoomIn,
                        modifier = Modifier.size(28.dp)
                    ) {
                        Text(
                            "+",
                            color = if (canZoomIn) canvasColors.hudTextPrimary else canvasColors.hudTextSecondary.copy(alpha = 0.35f),
                            fontSize = 18.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        }

        // HUD: Bottom-Left Controls (Virtual Joystick & Toggle)
        if (!gameState.isGameOver) {
            Column(
                modifier = Modifier
                    .padding(24.dp)
                    .align(Alignment.BottomStart),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                // Animated Virtual Joystick
                AnimatedVisibility(
                    visible = isJoystickActive,
                    enter = fadeIn(animationSpec = tween(220, easing = FastOutSlowInEasing)) +
                            scaleIn(
                                initialScale = 0.8f,
                                transformOrigin = TransformOrigin(0f, 1f),
                                animationSpec = tween(220, easing = FastOutSlowInEasing)
                            ) +
                            expandVertically(
                                expandFrom = Alignment.Bottom,
                                animationSpec = tween(220, easing = FastOutSlowInEasing)
                            ),
                    exit = fadeOut(animationSpec = tween(180, easing = FastOutSlowInEasing)) +
                            scaleOut(
                                targetScale = 0.8f,
                                transformOrigin = TransformOrigin(0f, 1f),
                                animationSpec = tween(180, easing = FastOutSlowInEasing)
                            ) +
                            shrinkVertically(
                                shrinkTowards = Alignment.Bottom,
                                animationSpec = tween(180, easing = FastOutSlowInEasing)
                            )
                ) {
                    VirtualJoystick(
                        onMove = { vector ->
                            joystickVector = vector
                        }
                    )
                }

                // Joystick Toggle Button / Pill
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = canvasColors.hudBackground
                    ),
                    shape = RoundedCornerShape(20.dp),
                    border = BorderStroke(
                        width = 1.dp,
                        color = if (isJoystickActive) Color(0xFF00E5FF).copy(alpha = 0.6f) else Color.White.copy(alpha = 0.15f)
                    ),
                    modifier = Modifier
                        .clip(RoundedCornerShape(20.dp))
                        .focusProperties { canFocus = false }
                        .clickable {
                            val nextState = !isJoystickActive
                            isJoystickActive = nextState
                            if (!nextState) {
                                joystickVector = Vector2D.ZERO
                            }
                            onToggleJoystick(nextState)
                            focusRequester.requestFocus()
                        }
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 7.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Text(
                            text = "🕹️",
                            fontSize = 14.sp
                        )
                        Text(
                            text = stringResource(Res.string.game_hud_joystick),
                            color = if (isJoystickActive) canvasColors.hudTextPrimary else canvasColors.hudTextSecondary,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold
                        )
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(if (isJoystickActive) Color(0xFF00E5FF) else Color(0xFF64748B))
                        )
                    }
                }
            }
        }

        // HUD: Bottom-Right Action Buttons (Split & Eject)
        val localPlayer = gameState.localPlayer
        val isSplitOnCooldown = gameState.splitCooldownRemainingMs > 0L
        val isEjectOnCooldown = gameState.ejectCooldownRemainingMs > 0L
        val canSplit = !uiState.isPaused && !gameState.isGameOver && localPlayer?.canSplit == true && !isSplitOnCooldown
        val canEject = !uiState.isPaused && !gameState.isGameOver && localPlayer?.canEjectMass == true && !isEjectOnCooldown

        Row(
            modifier = Modifier
                .padding(24.dp)
                .align(Alignment.BottomEnd),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Box(
                modifier = Modifier.size(88.dp),
                contentAlignment = Alignment.Center
            ) {
                Button(
                    onClick = {
                        val cameraTarget = gameState.cameraTarget
                        val zoom = (gameState.cameraZoom * userZoomFactor).coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
                        val targetWorld = getAimWorldPos(cameraTarget, zoom)
                        viewModel.onSplit(targetWorld)
                    },
                    enabled = canSplit,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFFFF9100),
                        disabledContainerColor = Color(0xFFFF9100).copy(alpha = 0.35f)
                    ),
                    shape = CircleShape,
                    contentPadding = PaddingValues(0.dp),
                    modifier = Modifier.fillMaxSize()
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        val splitTextColor = if (canSplit) Color.White else Color.White.copy(alpha = 0.45f)
                        Text(
                            text = stringResource(Res.string.game_btn_split),
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Black,
                            color = splitTextColor,
                            maxLines = 1,
                            softWrap = false
                        )
                        if (isSplitOnCooldown) {
                            Spacer(modifier = Modifier.height(2.dp))
                            val remainingSec = (gameState.splitCooldownRemainingMs + 99) / 1000f
                            Text(
                                text = "${(remainingSec * 10).toInt() / 10f}s",
                                fontSize = 11.sp,
                                fontWeight = FontWeight.ExtraBold,
                                color = Color.White.copy(alpha = 0.9f),
                                maxLines = 1,
                                softWrap = false
                            )
                        } else if (actionHints.splitSubtitle != null) {
                            Spacer(modifier = Modifier.height(2.dp))
                            Text(
                                text = actionHints.splitSubtitle,
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                color = splitTextColor.copy(alpha = if (canSplit) 0.9f else 0.45f),
                                maxLines = 1,
                                softWrap = false
                            )
                        }
                    }
                }

                if (isSplitOnCooldown) {
                    val sweep = 360f * (gameState.splitCooldownRemainingMs.toFloat() / GameConstants.SPLIT_ACTION_COOLDOWN_MS.toFloat())
                    Canvas(modifier = Modifier.fillMaxSize().padding(3.dp)) {
                        drawArc(
                            color = Color.White.copy(alpha = 0.85f),
                            startAngle = -90f,
                            sweepAngle = sweep,
                            useCenter = false,
                            style = Stroke(width = 3.dp.toPx(), cap = StrokeCap.Round)
                        )
                    }
                }
            }

            Box(
                modifier = Modifier.size(88.dp),
                contentAlignment = Alignment.Center
            ) {
                Button(
                    onClick = {
                        val cameraTarget = gameState.cameraTarget
                        val zoom = (gameState.cameraZoom * userZoomFactor).coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
                        val targetWorld = getAimWorldPos(cameraTarget, zoom)
                        viewModel.onEjectMass(targetWorld)
                    },
                    enabled = canEject,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFF00E5FF),
                        disabledContainerColor = Color(0xFF00E5FF).copy(alpha = 0.35f)
                    ),
                    shape = CircleShape,
                    contentPadding = PaddingValues(0.dp),
                    modifier = Modifier.fillMaxSize()
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        val ejectTextColor = if (canEject) Color.Black else Color.Black.copy(alpha = 0.45f)
                        Text(
                            text = stringResource(Res.string.game_btn_eject),
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Black,
                            color = ejectTextColor,
                            maxLines = 1,
                            softWrap = false
                        )
                        if (isEjectOnCooldown) {
                            Spacer(modifier = Modifier.height(2.dp))
                            val remainingSec = (gameState.ejectCooldownRemainingMs + 99) / 1000f
                            Text(
                                text = "${(remainingSec * 10).toInt() / 10f}s",
                                fontSize = 11.sp,
                                fontWeight = FontWeight.ExtraBold,
                                color = Color.White.copy(alpha = 0.9f),
                                maxLines = 1,
                                softWrap = false
                            )
                        } else if (actionHints.ejectSubtitle != null) {
                            Spacer(modifier = Modifier.height(2.dp))
                            Text(
                                text = actionHints.ejectSubtitle,
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                color = ejectTextColor.copy(alpha = if (canEject) 0.8f else 0.45f),
                                maxLines = 1,
                                softWrap = false
                            )
                        }
                    }
                }

                if (isEjectOnCooldown) {
                    val sweep = 360f * (gameState.ejectCooldownRemainingMs.toFloat() / GameConstants.EJECT_ACTION_COOLDOWN_MS.toFloat())
                    Canvas(modifier = Modifier.fillMaxSize().padding(3.dp)) {
                        drawArc(
                            color = Color.White.copy(alpha = 0.85f),
                            startAngle = -90f,
                            sweepAngle = sweep,
                            useCenter = false,
                            style = Stroke(width = 3.dp.toPx(), cap = StrokeCap.Round)
                        )
                    }
                }
            }
        }
    }

    // Game Over Overlay Dialog
    if (gameState.isGameOver) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.75f)),
            contentAlignment = Alignment.Center
        ) {
            Card(
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                shape = RoundedCornerShape(20.dp),
                modifier = Modifier
                    .windowInsetsPadding(WindowInsets.safeDrawing)
                    .widthIn(max = 340.dp)
                    .fillMaxWidth()
                    .padding(20.dp)
            ) {
                    Column(
                        modifier = Modifier.padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = stringResource(Res.string.game_over_title),
                            fontSize = 26.sp,
                            fontWeight = FontWeight.Black,
                            color = Color(0xFFFF1744)
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                        Text(
                            text = stringResource(Res.string.game_over_stats, uiState.finalMass, uiState.rank),
                            color = MaterialTheme.colorScheme.onSurface,
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp
                        )
                        Spacer(modifier = Modifier.height(20.dp))
                        Button(
                            onClick = { viewModel.onRestart() },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00E676)),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth().height(46.dp)
                        ) {
                            Text(stringResource(Res.string.game_over_play_again), fontWeight = FontWeight.Black, color = Color.Black)
                        }
                        Spacer(modifier = Modifier.height(10.dp))
                        OutlinedButton(
                            onClick = onExitToMenu,
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth().height(46.dp)
                        ) {
                            Text(stringResource(Res.string.game_over_main_menu), color = MaterialTheme.colorScheme.onSurface)
                        }
                    }
                }
            }
        }

    // Single Player Pause Overlay Dialog
    if (isSinglePlayer && uiState.isPaused && !gameState.isGameOver) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.70f))
                .pointerInput(Unit) {},
            contentAlignment = Alignment.Center
        ) {
            Card(
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                shape = RoundedCornerShape(20.dp),
                modifier = Modifier
                    .windowInsetsPadding(WindowInsets.safeDrawing)
                    .widthIn(max = 340.dp)
                    .fillMaxWidth()
                    .padding(20.dp)
            ) {
                Column(
                    modifier = Modifier.padding(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        text = stringResource(Res.string.game_paused_title),
                        fontSize = 24.sp,
                        fontWeight = FontWeight.Black,
                        color = Color(0xFF00E5FF)
                    )
                    Spacer(modifier = Modifier.height(10.dp))
                    gameState.localPlayer?.let { player ->
                        Text(
                            text = stringResource(Res.string.game_hud_mass, player.totalMass.toInt()),
                            color = MaterialTheme.colorScheme.onSurface,
                            fontWeight = FontWeight.Bold,
                            fontSize = 16.sp
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                    }
                    if (actionHints.showKeyboardPauseHint) {
                        Text(
                            text = stringResource(Res.string.game_paused_hint),
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            fontSize = 12.sp,
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(20.dp))
                    } else {
                        Spacer(modifier = Modifier.height(14.dp))
                    }

                    // Resume Button
                    Button(
                        onClick = { viewModel.resumeGame() },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00E676)),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(46.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.game_paused_resume),
                            fontWeight = FontWeight.Black,
                            color = Color.Black
                        )
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    // Restart Button
                    Button(
                        onClick = { viewModel.onRestart() },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2979FF)),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(46.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.game_paused_restart),
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }

                    Spacer(modifier = Modifier.height(10.dp))

                    // Exit to Menu Button
                    OutlinedButton(
                        onClick = { requestExit() },
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(46.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.game_paused_exit),
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            }
        }
    }

    // Exit Confirmation Dialog
    if (showExitConfirmDialog) {
            AlertDialog(
                onDismissRequest = { showExitConfirmDialog = false },
                title = {
                    Text(
                        text = stringResource(Res.string.game_exit_confirm_title),
                        fontWeight = FontWeight.Black,
                        fontSize = 20.sp,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                text = {
                    Text(
                        text = stringResource(Res.string.game_exit_confirm_message),
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                },
                confirmButton = {
                    Button(
                        onClick = {
                            showExitConfirmDialog = false
                            onExitToMenu()
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFF5252)),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.game_exit_confirm_leave),
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }
                },
                dismissButton = {
                    OutlinedButton(
                        onClick = { showExitConfirmDialog = false },
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.game_exit_confirm_stay),
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                },
                containerColor = MaterialTheme.colorScheme.surface,
                shape = RoundedCornerShape(20.dp)
            )
        }
        safetyTarget?.let { (playerId, playerName) ->
            if (BlobAiController.isAiPlayerId(playerId)) {
                AiPlayerDialog(
                    playerName = playerName,
                    onDismiss = { safetyTarget = null }
                )
            } else PlayerSafetyDialog(
                playerName = playerName,
                isBlocked = playerId in blockedIds,
                onDismiss = { safetyTarget = null },
                onReport = { reason: PlayerReportReason ->
                    val reporterUid = repository.getCurrentUserId().orEmpty()
                    repository.submitPlayerReport(
                        reporterUid = reporterUid,
                        reportedUid = playerId,
                        reportedNickname = playerName,
                        reason = reason.wireValue
                    )
                },
                onBlock = {
                    blockedPlayersStore.block(playerId, playerName)
                    safetyScope.launch {
                        val ownerUid = repository.getCurrentUserId().orEmpty()
                        if (ownerUid.isNotEmpty()) {
                            repository.setPlayerBlocked(ownerUid, playerId, playerName, blocked = true)
                        }
                    }
                },
                onUnblock = {
                    blockedPlayersStore.unblock(playerId)
                    safetyScope.launch {
                        val ownerUid = repository.getCurrentUserId().orEmpty()
                        if (ownerUid.isNotEmpty()) {
                            repository.setPlayerBlocked(ownerUid, playerId, playerName, blocked = false)
                        }
                    }
                }
            )
        }
    }

    // Docked bottom ad directly under the main game HUD on the very bottom
    if (bottomAdContent != null) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .windowInsetsPadding(
                    WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom + WindowInsetsSides.Horizontal)
                )
        ) {
            bottomAdContent()
        }
    }
    }
}

/**
 * Draws arena background grid and border boundaries transformed by dynamic camera.
 */
private fun DrawScope.drawArenaGrid(
    cameraTarget: Vector2D,
    zoom: Float,
    centerX: Float,
    centerY: Float,
    worldWidth: Float,
    worldHeight: Float,
    colors: GameCanvasColors
) {
    val arenaLeft = centerX + (0f - cameraTarget.x) * zoom
    val arenaTop = centerY + (0f - cameraTarget.y) * zoom
    val arenaRight = centerX + (worldWidth - cameraTarget.x) * zoom
    val arenaBottom = centerY + (worldHeight - cameraTarget.y) * zoom

    val arenaWidthPx = worldWidth * zoom
    val arenaHeightPx = worldHeight * zoom

    drawRect(
        color = colors.arenaBackground,
        topLeft = Offset(arenaLeft, arenaTop),
        size = Size(arenaWidthPx, arenaHeightPx)
    )

    // Grid lines
    val gridSize = GameConstants.GRID_SIZE * zoom
    if (gridSize > 8f) {
        var x = arenaLeft
        while (x <= arenaRight) {
            if (x >= 0 && x <= size.width) {
                drawLine(
                    color = colors.gridLines,
                    start = Offset(x, max(0f, arenaTop)),
                    end = Offset(x, min(size.height, arenaBottom)),
                    strokeWidth = 1f
                )
            }
            x += gridSize
        }

        var y = arenaTop
        while (y <= arenaBottom) {
            if (y >= 0 && y <= size.height) {
                drawLine(
                    color = colors.gridLines,
                    start = Offset(max(0f, arenaLeft), y),
                    end = Offset(min(size.width, arenaRight), y),
                    strokeWidth = 1f
                )
            }
            y += gridSize
        }
    }

    // Outer boundary walls
    drawRect(
        color = colors.arenaBorder,
        topLeft = Offset(arenaLeft, arenaTop),
        size = Size(arenaWidthPx, arenaHeightPx),
        style = Stroke(width = 4f * zoom)
    )
}

@Composable
fun VirtualJoystick(
    modifier: Modifier = Modifier,
    onMove: (Vector2D) -> Unit
) {
    val density = LocalDensity.current
    val baseSizeDp = 130.dp
    val knobSizeDp = 52.dp
    val maxTravelDp = 42.dp

    val maxTravelPx = with(density) { maxTravelDp.toPx() }

    var knobOffset by remember { mutableStateOf(Offset.Zero) }

    Box(
        modifier = modifier
            .size(baseSizeDp)
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    var activePointerId: PointerId? = null
                    val center = Offset(size.width / 2f, size.height / 2f)

                    while (true) {
                        val event = awaitPointerEvent()

                        if (activePointerId == null) {
                            val downPointer = event.changes.firstOrNull { it.pressed }
                            if (downPointer != null) {
                                activePointerId = downPointer.id
                                val delta = downPointer.position - center
                                val dist = kotlin.math.sqrt(delta.x * delta.x + delta.y * delta.y)
                                val clampedDist = min(dist, maxTravelPx)
                                val dir = if (dist > 0.001f) delta / dist else Offset.Zero
                                knobOffset = dir * clampedDist
                                val intensity = (clampedDist / maxTravelPx).coerceIn(0f, 1f)
                                onMove(Vector2D(dir.x * intensity, dir.y * intensity))
                                downPointer.consume()
                            }
                        } else {
                            val activePointer = event.changes.firstOrNull { it.id == activePointerId }
                            if (activePointer == null || !activePointer.pressed) {
                                activePointerId = null
                                knobOffset = Offset.Zero
                                onMove(Vector2D.ZERO)
                            } else {
                                val delta = activePointer.position - center
                                val dist = kotlin.math.sqrt(delta.x * delta.x + delta.y * delta.y)
                                val clampedDist = min(dist, maxTravelPx)
                                val dir = if (dist > 0.001f) delta / dist else Offset.Zero
                                knobOffset = dir * clampedDist
                                val intensity = (clampedDist / maxTravelPx).coerceIn(0f, 1f)
                                onMove(Vector2D(dir.x * intensity, dir.y * intensity))
                                activePointer.consume()
                            }
                        }
                    }
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            val center = Offset(size.width / 2f, size.height / 2f)
            val baseRadius = size.width / 2f

            // Outer base background
            drawCircle(
                color = Color(0x660F172A),
                radius = baseRadius,
                center = center
            )
            // Outer base stroke
            drawCircle(
                color = Color(0x6600E5FF),
                radius = baseRadius - 1f,
                center = center,
                style = Stroke(width = 2.dp.toPx())
            )
            // Intermediate guide ring
            drawCircle(
                color = Color(0x2200E5FF),
                radius = baseRadius * 0.55f,
                center = center,
                style = Stroke(width = 1.5.dp.toPx())
            )
            // Center resting dot
            drawCircle(
                color = Color(0x4400E5FF),
                radius = 4.dp.toPx(),
                center = center
            )

            // Thumb / Knob
            val knobCenter = center + knobOffset
            val knobRadius = (knobSizeDp / 2).toPx()

            // Outer halo
            drawCircle(
                color = Color(0x3300E5FF),
                radius = knobRadius + 4.dp.toPx(),
                center = knobCenter
            )
            // Knob body
            drawCircle(
                brush = Brush.radialGradient(
                    colors = listOf(Color(0xFF00E5FF), Color(0xFF0288D1)),
                    center = knobCenter - Offset(knobRadius * 0.25f, knobRadius * 0.25f),
                    radius = knobRadius * 1.2f
                ),
                radius = knobRadius,
                center = knobCenter
            )
            // Knob border
            drawCircle(
                color = Color.White.copy(alpha = 0.85f),
                radius = knobRadius,
                center = knobCenter,
                style = Stroke(width = 2.dp.toPx())
            )
            // Inner knob accent ring
            drawCircle(
                color = Color.White.copy(alpha = 0.5f),
                radius = knobRadius * 0.4f,
                center = knobCenter,
                style = Stroke(width = 1.5.dp.toPx())
            )
        }
    }
}
